import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { CalendarColumn } from "@/api/types";
import { Button, Drawer, Field, Input, Select } from "@/components/ui";
import { DateInput } from "@/components/DateInput";

const plus = (hhmm: string, min: number) => { const [h, m] = hhmm.split(":").map(Number); const tot = h * 60 + m + min; return `${String(Math.floor(tot / 60) % 24).padStart(2, "0")}:${String(tot % 60).padStart(2, "0")}`; };

/** Block a stretch of a day for one technician or for everyone (training, meeting, cleaning…). */
export function BlockDialog({ storeId, date, columns, staffId, time, onClose }: { storeId: string; date: string; columns: CalendarColumn[]; staffId?: string | null; time?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const start = time || "13:00";
  const [f, setF] = useState({ staff_id: staffId ?? "", date, start, end: plus(start, 60), reason: "" });
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api("/app/blocks", { method: "POST", body: JSON.stringify({ store_id: storeId, staff_id: f.staff_id || null, date: f.date, start_time: `${f.start}:00`, end_time: `${f.end}:00`, reason: f.reason || null }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["availability"] }); onClose(); },
    onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  return (
    <Drawer open onClose={onClose} title={t("blocks.title")}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (f.end <= f.start) { setError(t("blocks.endAfterStart")); return; } m.mutate(); }} className="space-y-3 text-sm">
        <Field label={t("appt.staff")}>
          <Select value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })}>
            <option value="">{t("blocks.all")}</option>
            {columns.map((c) => <option key={c.staff_id} value={c.staff_id}>{c.display_name}</option>)}
          </Select>
        </Field>
        <Field label={t("appt.date")}><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("blocks.start")}><Input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value, end: f.end <= e.target.value ? plus(e.target.value, 60) : f.end })} required /></Field>
          <Field label={t("blocks.end")}><Input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} required /></Field>
        </div>
        <Field label={t("blocks.reason")}><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder={t("blocks.reasonHint")} /></Field>
        <p className="text-xs text-muted">{t("blocks.hint")}</p>
        {error && <p className="text-danger">{error}</p>}
        <div className="flex gap-2"><Button disabled={m.isPending}>{t("app.save")}</Button><Button type="button" variant="ghost" onClick={onClose}>{t("app.cancel")}</Button></div>
      </form>
    </Drawer>
  );
}
